(() => {
  const SWIPE_THRESHOLD = 40;

  const initializeHero = (hero, reduceMotion) => {
    if (!hero || hero.dataset.bkHeroReady === 'true') return;
    hero.dataset.bkHeroReady = 'true';

    const slides = [...hero.querySelectorAll('[data-bk-hero-slide]')];
    const dots = [...hero.querySelectorAll('[data-bk-hero-dot]')];
    const previousZone = hero.querySelector('[data-bk-hero-prev]');
    const nextZone = hero.querySelector('[data-bk-hero-next]');
    const interval = Math.max(Number(hero.dataset.bkHeroInterval) || 6500, 2500);
    const duration = reduceMotion ? 0 : 650;
    let activeSlide = 0;
    let timer;
    let settleTimer;
    let swipeStart = null;
    let suppressClick = false;

    const paint = () => {
      slides.forEach((slide, index) => {
        slide.setAttribute('aria-hidden', String(index !== activeSlide));
      });
      dots.forEach((dot, index) => {
        const isActive = index === activeSlide;
        dot.classList.toggle('is-active', isActive);
        if (isActive) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    };

    // Slides the incoming panel in from the side it logically lives on and pushes the
    // outgoing panel out the other way, so "next" always travels left and "previous" right
    // (including the wrap from the last slide back to the first).
    const goTo = (target, direction) => {
      if (slides.length < 2) return;
      const next = (target + slides.length) % slides.length;
      if (next === activeSlide) return;
      const travel = direction || (next > activeSlide ? 1 : -1);
      const outgoing = slides[activeSlide];
      const incoming = slides[next];

      window.clearTimeout(settleTimer);
      slides.forEach((slide) => {
        if (slide !== outgoing && slide !== incoming) {
          slide.classList.remove('is-leaving', 'is-active');
          slide.style.transform = '';
        }
      });

      incoming.classList.add('is-entering');
      incoming.classList.remove('is-leaving');
      incoming.style.transform = `translateX(${travel * 100}%)`;
      void incoming.offsetWidth; // flush the start position before animating
      incoming.classList.remove('is-entering');
      incoming.classList.add('is-active');
      incoming.style.transform = '';

      outgoing.classList.add('is-leaving');
      outgoing.classList.remove('is-active');
      outgoing.style.transform = `translateX(${travel * -100}%)`;

      activeSlide = next;
      paint();

      settleTimer = window.setTimeout(() => {
        outgoing.classList.remove('is-leaving');
        outgoing.style.transform = '';
      }, duration + 20);
    };

    const stopRotation = () => window.clearInterval(timer);
    const startRotation = () => {
      stopRotation();
      if (reduceMotion || slides.length < 2 || document.hidden) return;
      timer = window.setInterval(() => goTo(activeSlide + 1, 1), interval);
    };
    const step = (direction) => {
      goTo(activeSlide + direction, direction);
      startRotation();
    };

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        goTo(Number(dot.dataset.bkHeroDot));
        startRotation();
      });
    });
    previousZone?.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); return; }
      step(-1);
    });
    nextZone?.addEventListener('click', (event) => {
      if (suppressClick) { event.preventDefault(); return; }
      step(1);
    });

    hero.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowLeft') { event.preventDefault(); step(-1); }
      if (event.key === 'ArrowRight') { event.preventDefault(); step(1); }
    });

    // Swipe: touch events for fingers, pointer events for mouse/pen drags. Vertical
    // scrolling still works because the hero sets touch-action: pan-y; a horizontal
    // drag past the threshold moves one slide and swallows the click that may follow.
    const settleSwipe = (dx, dy) => {
      if (Math.abs(dx) < SWIPE_THRESHOLD || Math.abs(dx) <= Math.abs(dy)) return;
      suppressClick = true;
      window.setTimeout(() => { suppressClick = false; }, 350);
      step(dx < 0 ? 1 : -1);
    };
    hero.addEventListener('touchstart', (event) => {
      const touch = event.changedTouches[0];
      swipeStart = { x: touch.clientX, y: touch.clientY, touch: true };
      suppressClick = false;
    }, { passive: true });
    hero.addEventListener('touchend', (event) => {
      if (!swipeStart?.touch) return;
      const touch = event.changedTouches[0];
      const start = swipeStart;
      swipeStart = null;
      settleSwipe(touch.clientX - start.x, touch.clientY - start.y);
    }, { passive: true });
    hero.addEventListener('touchcancel', () => { swipeStart = null; }, { passive: true });
    hero.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button !== 0) return;
      swipeStart = { x: event.clientX, y: event.clientY, id: event.pointerId };
      suppressClick = false;
    });
    hero.addEventListener('pointerup', (event) => {
      if (!swipeStart || swipeStart.touch || event.pointerId !== swipeStart.id) return;
      const start = swipeStart;
      swipeStart = null;
      settleSwipe(event.clientX - start.x, event.clientY - start.y);
    });
    hero.addEventListener('pointercancel', () => { if (!swipeStart?.touch) swipeStart = null; });
    hero.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopPropagation();
    }, true);

    // Keep auto-play running on hover; pause only for keyboard focus inside the hero and
    // while the tab is hidden, and pick the timer back up afterwards.
    hero.addEventListener('focusin', stopRotation);
    hero.addEventListener('focusout', (event) => {
      if (!hero.contains(event.relatedTarget)) startRotation();
    });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopRotation();
      else startRotation();
    });

    paint();
    startRotation();
  };

  const initializeHome = (root) => {
    if (!root || root.dataset.bkInitialized === 'true') return;
    root.dataset.bkInitialized = 'true';
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    root.querySelectorAll('[data-bk-hero]').forEach((hero) => initializeHero(hero, reduceMotion));

    root.querySelectorAll('.bk-product-rail-section').forEach((section) => {
      const rail = section.querySelector('[data-bk-product-rail]');
      const distance = () => Math.max(rail?.clientWidth * 0.82 || 320, 320);
      section.querySelector('[data-bk-rail-prev]')?.addEventListener('click', () => {
        rail?.scrollBy({ left: -distance(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      section.querySelector('[data-bk-rail-next]')?.addEventListener('click', () => {
        rail?.scrollBy({ left: distance(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
    });
  };

  const initializeAll = () => {
    document.querySelectorAll('[data-bk-editorial-home]').forEach(initializeHome);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeAll, { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener('shopify:section:load', initializeAll);
})();
